<template>
  <InfuseSpinner class="flex items-center justify-center p-16" v-if="isCentralSystemsLoading" size="medium" />

  <div v-else>
    <InfuseNote class="mb-8" v-if="!isCentralSystemsLoading && linkedCentralSystems.length === 0 && !searchQuery">
      {{ t('e_connector.chargingStationLinks.noLinkedCentralSystems') }}
    </InfuseNote>

    <LinkedCentralSystemTable
      :key="tableKey"
      :create-enabled="true"
      :editable="true"
      :loading="isCentralSystemsLoading"
      :removable="true"
      :rows="filteredLinkedCentralSystems"
      :search-enabled="true"
      :search-query="searchQuery"
      :selectable="true"
      @create="createModalVisible = true"
      @edit="editRow"
      @remove="(linkedCentralSystem: LinkedCentralSystem) => confirmRemove(linkedCentralSystem)"
      @search="(query: string) => search(query)"
      @bulk-edit="(rows: LinkedCentralSystemHydrated[]) => bulkEdit(rows)"
      @bulk-remove="(rows: LinkedCentralSystemHydrated[]) => confirmBulkRemove(rows)"
      @make-master="(row: LinkedCentralSystemHydrated) => confirmMakeMaster(row)"
    />

    <InfuseNote class="mt-4" v-if="inaccessibleCount > 0">
      {{ t('e_connector.chargingStationLinks.inaccessibleCentralSystems', { count: inaccessibleCount }) }}
    </InfuseNote>
  </div>

  <CreateLinkedCentralSystemModal
    v-if="createModalVisible"
    :loading="isUpserting || isCreatingCentralSystem"
    :central-systems="centralSystems"
    :charging-station="model"
    @close="createModalVisible = false"
    @create="
      (linkedCentralSystem: LinkedCentralSystem, newCentralSystem?: NewCentralSystemDefinition) =>
        create(linkedCentralSystem, newCentralSystem)
    "
  />

  <UpdateLinkedCentralSystemModal
    v-if="updateModalVisible && selectedRow"
    :loading="isUpserting"
    :linked-central-system="selectedRow"
    :central-systems="centralSystems"
    :charging-station="model"
    @close="updateModalVisible = false"
    @update="(linkedCentralSystem: LinkedCentralSystem) => update(linkedCentralSystem)"
  />

  <InfuseConfirmModal
    v-if="removeModalVisible"
    :action-title="t('e_connector.chargingStationLinks.modal.remove.title')"
    verification-text="delete"
    @confirm="remove"
    @close="removeModalVisible = false"
  />

  <BulkUpdateLinkedCentralSystemsModal
    v-if="bulkUpdateModalVisible"
    :loading="isBulkUpserting"
    :linked-central-systems="selectedRows"
    @close="bulkUpdateModalVisible = false"
    @update="(changes: BulkLinkedCentralSystemChanges) => bulkUpdate(changes)"
  />

  <InfuseConfirmModal
    v-if="bulkRemoveModalVisible"
    :action-title="t('e_connector.chargingStationLinks.modal.bulkRemove.title')"
    :action-description="t('e_connector.chargingStationLinks.modal.bulkRemove.description', { count: selectedRows.length })"
    verification-text="delete"
    :loading="isBulkUpserting"
    @confirm="bulkRemove"
    @close="bulkRemoveModalVisible = false"
  />

  <InfuseConfirmModal
    v-if="makeMasterModalVisible && makeMasterRow"
    :action-title="t('e_connector.chargingStationLinks.modal.makeMaster.title')"
    :action-description="
      currentMasterRow
        ? t('e_connector.chargingStationLinks.modal.makeMaster.description', {
            name: makeMasterRow.centralSystem.name,
            currentName: currentMasterRow.centralSystem.name
          })
        : t('e_connector.chargingStationLinks.modal.makeMaster.descriptionNoMaster', {
            name: makeMasterRow.centralSystem.name
          })
    "
    :verification-required="false"
    :loading="isSwappingMaster"
    @confirm="makeMaster"
    @close="makeMasterModalVisible = false"
  />
</template>

<script setup lang="ts">
import { ConnectionType, LinkedCentralSystem, LinkedCentralSystemType } from '@infuse/e-connector/central-system/domain';
import {
  useBulkUpsertCentralSystemLinks,
  useCreateCentralSystem,
  useSwapMasterCentralSystemLink,
  useUpsertCentralSystemLink
} from '@infuse/e-connector/central-system/mutations';
import { useCentralSystems } from '@infuse/e-connector/central-system/queries';
import { ChargingStation } from '@infuse/e-connector/charging-station/domain';
import {
  BulkLinkedCentralSystemChanges,
  BulkUpdateLinkedCentralSystemsModal,
  CreateLinkedCentralSystemModal,
  LinkedCentralSystemTable,
  NewCentralSystemDefinition,
  UpdateLinkedCentralSystemModal
} from '@infuse/e-connector/charging-station/ui';
import { hydrateLinkedCentralSystems, LinkedCentralSystemHydrated } from '@infuse/e-connector/shared/feature';
import { ECONNECTOR_MAX_PAGE_SIZE } from '@infuse/e-connector/shared/queries';
import { InfuseConfirmModal, InfuseNote, InfuseSpinner } from '@infuse/ui/components';
import { replaceQueryParams } from '@infuse/ui/utils';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const { t } = useI18n();

const route = useRoute();
const router = useRouter();

const {
  centralSystems,
  isLoading: isCentralSystemsLoading,
  fetchAllPages: fetchAllCentralSystems
} = useCentralSystems(ECONNECTOR_MAX_PAGE_SIZE); // load all central systems for linking
const { mutateAsync: upsertCentralSystemLink, isPending: isUpserting } = useUpsertCentralSystemLink();
const { mutateAsync: bulkUpsertCentralSystemLinks, isPending: isBulkUpserting } = useBulkUpsertCentralSystemLinks();
const { mutateAsync: swapMasterCentralSystemLink, isPending: isSwappingMaster } = useSwapMasterCentralSystemLink();
const { mutateAsync: createCentralSystem, isPending: isCreatingCentralSystem } = useCreateCentralSystem();

const searchQuery = ref<string | undefined>(route.query.search?.toString());

const createModalVisible = ref<boolean>(false);
const updateModalVisible = ref<boolean>(false);
const removeModalVisible = ref<boolean>(false);
const bulkUpdateModalVisible = ref<boolean>(false);
const bulkRemoveModalVisible = ref<boolean>(false);
const makeMasterModalVisible = ref<boolean>(false);
const selectedRow = ref<LinkedCentralSystem>();
const selectedRows = ref<LinkedCentralSystemHydrated[]>([]);
const makeMasterRow = ref<LinkedCentralSystemHydrated>();
// InfuseTable has no external API to clear its selection; bump the key to remount after a bulk action
const tableKey = ref<number>(0);

const linkedCentralSystems = ref<LinkedCentralSystemHydrated[]>([]);
const filteredLinkedCentralSystems = ref<LinkedCentralSystemHydrated[]>([]);
const inaccessibleCount = ref<number>(0);

interface Emits {
  (e: 'reload-model'): void;
}
const emit = defineEmits<Emits>();

interface Props {
  model: ChargingStation;
}
const props = withDefaults(defineProps<Props>(), {});

onMounted(async () => {
  await fetchAllCentralSystems().then(() => {
    rehydrateLinkedCentralSystems();
  });
});

// rehydrate when the charging station is refetched after linking/unlinking a central system,
// and when the central systems list is refetched (e.g. after defining a new central system)
watch(() => props.model, rehydrateLinkedCentralSystems);
watch(centralSystems, rehydrateLinkedCentralSystems);

async function search(search: string) {
  // local search since we use the LinkedCentralSystem model, not the paginated central systems directly
  searchQuery.value = search;
  replaceQueryParams(router, 'charging-stations.linkedSystems', { search: searchQuery.value });

  applySearchFilter();
}

function applySearchFilter() {
  const search = searchQuery.value;

  if (search) {
    filteredLinkedCentralSystems.value = linkedCentralSystems.value.filter((linkedCentralSystem) =>
      [
        ...Object.values(linkedCentralSystem),
        ...Object.values(linkedCentralSystem.chargingStation),
        ...Object.values(linkedCentralSystem.centralSystem)
      ].some((v) => v?.toString().toLowerCase().includes(search.toLowerCase()))
    );
  } else {
    filteredLinkedCentralSystems.value = [...linkedCentralSystems.value];
  }
}

async function create(linkedCentralSystem: LinkedCentralSystem, newCentralSystem?: NewCentralSystemDefinition) {
  if (newCentralSystem) {
    const created = await createCentralSystem({
      name: newCentralSystem.name,
      uri: newCentralSystem.uri,
      type: ConnectionType.WEBSOCKET,
      ocppVersion: newCentralSystem.ocppVersion
    });
    linkedCentralSystem = { ...linkedCentralSystem, centralSystemId: created.id };
  }

  await upsertCentralSystemLink({ linkedCentralSystem });
  createModalVisible.value = false;
  emit('reload-model');
}

async function update(linkedCentralSystem: LinkedCentralSystem) {
  await upsertCentralSystemLink({ linkedCentralSystem });
  updateModalVisible.value = false;
  emit('reload-model');
}

function editRow(row: LinkedCentralSystem) {
  selectedRow.value = row;
  updateModalVisible.value = true;
}

function confirmRemove(row: LinkedCentralSystem) {
  selectedRow.value = row;
  removeModalVisible.value = true;
}

function bulkEdit(rows: LinkedCentralSystemHydrated[]) {
  selectedRows.value = rows;
  bulkUpdateModalVisible.value = true;
}

function confirmBulkRemove(rows: LinkedCentralSystemHydrated[]) {
  selectedRows.value = rows;
  bulkRemoveModalVisible.value = true;
}

async function bulkUpdate(changes: BulkLinkedCentralSystemChanges) {
  await bulkUpsertCentralSystemLinks({
    links: selectedRows.value.map((row) => ({
      linkedCentralSystem: { ...row, ...changes },
      centralSystemName: row.centralSystem.name
    }))
  });

  bulkUpdateModalVisible.value = false;
  clearSelection();
  emit('reload-model');
}

async function bulkRemove() {
  await bulkUpsertCentralSystemLinks({
    links: selectedRows.value.map((row) => ({
      linkedCentralSystem: row,
      centralSystemName: row.centralSystem.name
    })),
    shouldRemoveLink: true
  });

  bulkRemoveModalVisible.value = false;
  clearSelection();
  emit('reload-model');
}

function clearSelection() {
  selectedRows.value = [];
  tableKey.value++;
}

const currentMasterRow = computed(() =>
  linkedCentralSystems.value.find((lcs) => lcs.type === LinkedCentralSystemType.MASTER)
);

function confirmMakeMaster(row: LinkedCentralSystemHydrated) {
  makeMasterRow.value = row;
  makeMasterModalVisible.value = true;
}

async function makeMaster() {
  if (!makeMasterRow.value) {
    return;
  }

  const target = makeMasterRow.value;
  const current = currentMasterRow.value;

  await swapMasterCentralSystemLink({
    newMaster: { linkedCentralSystem: target, centralSystemName: target.centralSystem.name },
    currentMaster: current
      ? { linkedCentralSystem: current, centralSystemName: current.centralSystem.name }
      : undefined
  });

  makeMasterModalVisible.value = false;
  makeMasterRow.value = undefined;
  emit('reload-model');
}

async function remove() {
  if (!selectedRow.value) {
    return;
  }

  await upsertCentralSystemLink({ linkedCentralSystem: selectedRow.value, shouldRemoveLink: true });

  removeModalVisible.value = false;
  selectedRow.value = undefined;
  emit('reload-model');
}

function rehydrateLinkedCentralSystems() {
  if (props.model) {
    linkedCentralSystems.value = hydrateLinkedCentralSystems(props.model, centralSystems.value) ?? [];
    // links whose central system is not in the fetched list (IAM-filtered) are omitted by the hydration
    inaccessibleCount.value = props.model.linkedCentralSystems.length - linkedCentralSystems.value.length;
    applySearchFilter();
  } else {
    console.warn('No charging station provided?');
  }
}
</script>
